import React, { useEffect, useMemo, useRef } from 'react';
import { ISeriesApi } from 'lightweight-charts';
import { useChartPositionContext } from '../contexts/ChartPositionContext';
import ChartLevelOverlay from './ChartLevelOverlay';

interface ChartPositionManagerProps {
  seriesRef: ISeriesApi<any> | null;
  symbol: string;
  /** Live price, used to classify a dragged resting order as a limit or a stop. */
  currentPrice: number;
  isVisible: boolean;
}

/**
 * Draws each position's entry / take-profit / stop-loss on the chart.
 *
 * Every part of a level — its line, its controls and its price tag on the axis — is drawn by
 * `ChartLevelOverlay` as DOM on top of the canvas, positioned through the series' own
 * coordinate conversion.
 *
 * Levels used to be drawn twice: a lightweight-charts price line as well as the overlay. The
 * library's line spans the full width of the pane, so a position put a rule straight through
 * the candles from the left edge of the chart, and the overlay then drew its own short line
 * over the last stretch of it. One level, two lines, in slightly different shades. Only the
 * overlay's is kept, which is why nothing here touches the series any more.
 */
const ChartPositionManager: React.FC<ChartPositionManagerProps> = ({
  seriesRef,
  symbol,
  currentPrice,
  isVisible
}) => {
  const { chartPositions, syncWithPositions, syncWithPendingOrders } = useChartPositionContext();
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    syncWithPositions(symbol);
    syncWithPendingOrders(symbol);
  }, [symbol, syncWithPositions, syncWithPendingOrders]);

  const symbolPositions = useMemo(
    () => chartPositions.filter(pos => pos.symbol === symbol && pos.isVisible !== false),
    [chartPositions, symbol]
  );

  if (!isVisible || !seriesRef || symbolPositions.length === 0) {
    return null;
  }

  return (
    <div ref={overlayRef} className="absolute inset-0 pointer-events-none chartpositions" style={{ zIndex: 20 }}>
      {symbolPositions.map(position => (
        <ChartLevelOverlay
          key={position.id}
          position={position}
          series={seriesRef}
          currentPrice={currentPrice}
          overlayRef={overlayRef}
        />
      ))}
    </div>
  );
};

export default ChartPositionManager;
